import styled from "@emotion/styled";
import { color } from "@smartsheet/design-tokens-react";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { createFocusRingStyles } from "../../../../util/createFocusRingStyles";
import { getInputFieldHeightValue, InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { withFocusToggleClassName } from "../../../../util/withFocusClassName";
import { FocusMode } from "../../../focusMode";
import { formInputTheme } from "../../../textinput/styled/formInputTheme";
import { selectV2Theme } from "../selectV2Theme";

interface RootProps extends InputFieldBaseProps {
    focusMode: FocusMode;
    isFocused: boolean;
    isReadonly: boolean;
    themeMode: RequiredThemeKeys;
    validationState: "error" | "rest";
    appearance?: "outlined" | "underlined" | "borderless";
    omitFocusRing?: boolean;
}

export const Root = withFocusToggleClassName(styled("div")<RootProps>`
    position: relative;
    padding-right: var(--sds--selectv2--wrapper--padding-right, 0px);
    padding-top: var(--sds--selectv2--wrapper--padding-top, 0px);
    padding-left: var(--sds--selectv2--wrapper--padding-left, 0px);
    padding-bottom: var(--sds--selectv2--wrapper--padding-bottom, 0px);

    // To allow certain elements within here to use z-index without breaking stacking context.
    isolation: isolate;

    // Calling this out explicitly so people notice.
    /* box-sizing: content-box; */
    box-sizing: border-box;

    display: flex;
    flex-direction: row;
    align-items: center;
    min-height: ${({ themeMode, inputFieldHeight }) =>
        `var(--sds--form--height--${inputFieldHeight === 32 ? "default" : "large"}, ${formInputTheme.height[
            getInputFieldHeightValue(themeMode, inputFieldHeight)
        ](themeMode)}px)`};
    background-color: ${getBackgroundColor};
    color: ${color("color--text--neutral--strong")};

    border-radius: ${({ themeMode }) => `var(--sds--form--border-radius, ${formInputTheme.borderRadius(themeMode)})`};

    ${({ isFocused, omitFocusRing, themeMode }) =>
        isFocused && !omitFocusRing && formInputTheme.focusRing.enabled(themeMode)
            ? createFocusRingStyles({
                  theme: themeMode,
                  gap: 1,
                  // This is kinda weird, but by using an empty string here, we defer the show/hide
                  // behavior to the ternary statement above.
                  focusSelector: ``,
                  componentBorderWidth: 0,
                  componentRadius: `var(--sds--form--border-radius, ${formInputTheme.borderRadius(themeMode)})`,
              })
            : ""}

    // We're going to use a second ring here to fake the border. Doing this allows us to avoid a lot
    // of positioning issues due to the border width changes necessary for Ursa. Since there are a
    // number of elements that add some margin-top, left, top, etc., they all need to be adjusted
    // when validationState changes in Ursa. Lodestar doesn't have this problem. Using this method,
    // this "border" will overlay the outer edge of the SelectV2 and inner elements will be
    // positioned against the outer edge of the root element which now has no actual border.
    ${(props) =>
        createFocusRingStyles({
            theme: props.themeMode,
            gap: -formInputTheme.borderWidth[props.validationState](props.themeMode),
            // Always show this ring.
            focusSelector: ``,
            componentBorderWidth: 0,
            componentRadius: formInputTheme.borderRadius(props.themeMode),
            innerColorOverride: "transparent",
            colorOverride: getBorderColor(props),
            ringWidth: getBorderWidth(props),
            pseudoElement: "after",
        })}

    ${(props) =>
        props.appearance === "underlined" &&
        `
        // specific styles for the underlined appearance's focus ring
        &::before {
            // Needed to ensure the focus ring and white offset ring cover the underline
            z-index: 1;
        }

        // specific styles for the underlined appearance's "border"
        &::after {
            // this overrides the border width set in createFocusRingStyles
            border-width: 0;
            border-radius: 0;
            ${getBorderRule(props)}-width: ${getBorderWidth(props)}px;

            ${
                props.isFocused
                    ? `
                        // to hide the tiny triangle sliver of underline peeking out in Ursa
                        right: ${selectV2Theme.border.focusedInset(props.themeMode)};
                        left: ${selectV2Theme.border.focusedInset(props.themeMode)};
                        // For Lodestar flat underline but rounded focus ring
                        border-radius: ${selectV2Theme.border.focusedRadius(props.themeMode)};
                    `
                    : ``
            }
        }
        `}
    
    ${(props) =>
        props.appearance === "borderless" &&
        (formInputTheme.focusRing.enabled(props.themeMode) || !props.isFocused) &&
        `
            &::after {
                border: none;
            }
        `}
    
    ${({ isFocused, themeMode, validationState, isReadonly }) => {
        if (!isFocused && !isReadonly && validationState === "rest") {
            return `
                &:hover:after {
                    border-color: var(--sds--form--border-color--hover, ${formInputTheme.borderColor.hover(themeMode)});
                }
            `;
        }
    }}

    ${({ isReadonly }) => {
        return (
            isReadonly &&
            `
                cursor: not-allowed;
            `
        );
    }}
`);

function getBorderWidth({ themeMode, validationState, isFocused, focusMode }: RootProps) {
    if (validationState === "error") {
        return formInputTheme.borderWidth.error(themeMode);
    } else if (isFocused && focusMode !== "keyboard") {
        return formInputTheme.borderWidth.active(themeMode);
    } else {
        return formInputTheme.borderWidth.rest(themeMode);
    }
}

function getBackgroundColor({ isReadonly, themeMode, validationState, appearance }: RootProps) {
    if (appearance === "underlined") {
        return formInputTheme.backgroundColor.underlined(themeMode);
    }
    if (isReadonly) {
        return formInputTheme.backgroundColor.readonly(themeMode);
    } else if (validationState === "error") {
        return formInputTheme.backgroundColor.error(themeMode);
    } else {
        return formInputTheme.backgroundColor.rest(themeMode);
    }
}

function getBorderColor({ isFocused, themeMode, validationState, isReadonly, focusMode }: RootProps) {
    const isKeyboardFocused = focusMode === "keyboard";

    if (validationState === "error" && isFocused) {
        return `var(--sds--form--border-color--error, ${formInputTheme.borderColor.errorFocus(themeMode)})`;
    } else if (isReadonly) {
        return `var(--sds--form--border-color--disabled, ${formInputTheme.borderColor.readonly(themeMode)})`;
    } else if (isFocused && !isKeyboardFocused) {
        return `var(--sds--form--border-color--active, ${formInputTheme.borderColor.focus(themeMode)})`;
    } else if (isFocused && isKeyboardFocused) {
        return `var(--sds--form--border-color--rest, ${formInputTheme.borderColor.rest(themeMode)})`;
    } else if (validationState === "error") {
        return `var(--sds--form--border-color--error, ${formInputTheme.borderColor.error(themeMode)})`;
    } else if (isFocused) {
        return formInputTheme.borderColor.focus(themeMode);
    } else {
        return `var(--sds--form--border-color--rest, ${formInputTheme.borderColor.rest(themeMode)})`;
    }
}

function getBorderRule({ appearance, isFocused, themeMode }: RootProps) {
    return appearance === "underlined" && !isFocused
        ? "border-bottom"
        : appearance === "underlined"
        ? selectV2Theme.border.focusedRule(themeMode)
        : "border";
}
